import { Fragment, memo } from "react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { Lead } from "views/leads-id/types";
import { StarCell } from "shared/app/LeadsTableView/slices/LeadsTable/components/StarCell";
import { WaitlistLeadIndicator } from "shared/app/LeadsTableView/components/WaitlistLeadIndicator";
import { LEAD_ACTIVITY_TYPE_LABELS } from "shared/app/LeadsTableView/slices/LeadsTable/constants";
import { ApprovalLikelihoodBadge } from "shared/app/LeadsTableView/components/ApprovalLikelihoodBadge";

interface PipelineCardProps {
  lead: Lead;
  isDragging?: boolean;
  isLeadScoringEnabled: boolean;
}

const formatActivityDate = (date?: string | null) => {
  if (!date) return "";
  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.toFormat("MMM d") : "";
};

export const PipelineCard = memo(
  ({ lead, isDragging, isLeadScoringEnabled }: PipelineCardProps) => {
    const navigate = useNavigate();

    const assigneeName =
      lead.assignees?.[0]?.name || lead.assigned_to_user?.name;
    const recentActivities = lead.recent_activities ?? [];
    const source = lead.source;

    return (
      <Card
        onClick={() => navigate(`/leads/${lead.id}`)}
        className={cx(
          "cursor-grab space-y-2 p-3 active:cursor-grabbing",
          isDragging ? "shadow-lg" : ""
        )}
      >
        <Show
          if={
            isLeadScoringEnabled &&
            (Boolean(lead.approval_likelihood_band) ||
              Boolean(lead.approval_likelihood_insufficient_data))
          }
        >
          <ApprovalLikelihoodBadge
            band={lead.approval_likelihood_band}
            insufficientData={lead.approval_likelihood_insufficient_data}
          />
        </Show>

        <div className="space-y-1">
          <p className="truncate text-sm font-bold text-content-1000">
            {lead.first_name} {lead.last_name}
          </p>
          <div className="flex items-center gap-1.5 min-w-0 text-xs text-content-400">
            <Show if={Boolean(lead.property?.name)}>
              <span className="truncate">{lead.property?.name}</span>
            </Show>
            <Show if={lead.from_waitlist}>
              <Fragment>
                <Show if={Boolean(lead.property?.name)}>
                  <span aria-hidden="true">·</span>
                </Show>
                <WaitlistLeadIndicator
                  unitTitle={lead.unit?.title}
                  joinedAt={lead.created_at}
                />
                <Show if={Boolean(lead.unit?.title)}>
                  <span className="truncate">{lead.unit?.title}</span>
                </Show>
              </Fragment>
            </Show>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Show if={Boolean(source?.name)}>
            <Badge
              variant={BadgeVariant.OUTLINE}
              size={BadgeSize.SM}
              leftElement={
                <Avatar
                  fallbackText=""
                  fallbackBackgroundColor={source?.color ?? undefined}
                  size={8}
                />
              }
              rightElement={
                <span className="text-xs font-medium text-content-1000">
                  {source?.name}
                </span>
              }
              className="!gap-2 items-center h-6"
            />
          </Show>
          <div className="ml-auto flex items-center gap-1">
            <div className="size-8">
              <StarCell leadId={lead.id} starred={lead.starred} />
            </div>
            <Show if={Boolean(assigneeName)}>
              <Avatar
                name={assigneeName}
                size={24}
                className="text-[10px] text-content-1000-inverted"
              />
            </Show>
          </div>
        </div>

        <Show if={recentActivities.length > 0}>
          <div className="space-y-2 border-t border-alpha-100 pt-3">
            <p className="text-xs font-medium text-content-300">
              Recent activity
            </p>
            <div className="space-y-1.5">
              {recentActivities.map((activity) => {
                const label =
                  LEAD_ACTIVITY_TYPE_LABELS[activity.activity_type] ??
                  activity.title;
                return (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <p className="truncate font-medium text-xs text-content-1000">
                      {activity.actor_name
                        ? `${label} by ${activity.actor_name}`
                        : label}
                    </p>
                    <p className="shrink-0 font-medium text-xs text-content-300">
                      {formatActivityDate(activity.occurred_at)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </Show>
      </Card>
    );
  }
);

PipelineCard.displayName = "PipelineCard";
